<script setup lang="ts">
import {computed, onBeforeMount, reactive} from "vue";
import activiteitRepository, {ActiviteitItem, BekostigingsType} from "@/repositories/activiteit.repository";
import {usePlatformStore} from "@/stores/platform.store";
import {isBefore,isAfter} from "date-fns";
import {Tooltip} from "floating-vue";
import Loader from "@/components/Loader.vue";
import RedirectPlatformModal from "@/components/Modals/RedirectPlatformModal.vue";
import {notify} from "@kyvg/vue3-notification";
import {getDateInHoursAndMinutes, getDateForRegistrationClosed} from "@/helpers/date.helper"

const platformStore = usePlatformStore();

const props = defineProps<{
    id: number,
    activitySingular: string,
    activityPlural: string,
}>();

const state = reactive<{
    isLoading: boolean,
    redirect: boolean,
    activity?: ActiviteitItem,
    bookingUrl: string,
    activityUrl: string,
    availableSpots: number,
}>({
    isLoading: false,
    redirect: false,
    bookingUrl: "",
    activityUrl: "",
    availableSpots: 0,
});

const registrationStatusClass = computed(() => {
    const prefix = " sport-card-refined__status--";
    if (state.availableSpots <= 0) {
        return prefix + "danger";
    }
    if (state.availableSpots < 3) {
        return prefix + "warning";
    }
    return prefix + "success";
});

const calculateAvailableSpots = computed(() => {
    if (!state.activity) return 0;
    if (state.activity.AantalPlaatsen <= state.activity.AantalInschrijvingen) return 0;
    
    if (state.activity.TotaalAantalPlaatsen != state.activity.AantalPlaatsen) {
        const availablePlaces = state.activity.TotaalAantalPlaatsen - state.activity.AantalPlaatsen;
        const availablePlacesPlatform = state.activity.AantalPlaatsen - state.activity.AantalInschrijvingen;
        
        if ((availablePlaces > state.activity.AantalPlaatsen) || (availablePlaces > availablePlacesPlatform)) {
            return availablePlacesPlatform;
        }
        
        return availablePlaces
    }
    
    return state.activity.TotaalAantalPlaatsen - state.activity.TotaalAantalInschrijvingen;
});

const availableSpotsText = computed(() => {
    if (!state.activity) return '';

    const totalSpaces = state.activity.TotaalAantalPlaatsen;
    const isPlural = totalSpaces > 1;
    return `${state.availableSpots}/${totalSpaces} plek${isPlural ? 'ken' : ''} vrij`;
});

const isSamePlatform = computed(() => {
    if(!state.activity) return false;
    
    const activityUrl = new URL(state.activity.HoofdplatformLink);
    const currentUrl = new URL(window.location.href);
    return activityUrl.hostname === currentUrl.hostname;
});

const startRedirect = () => {
    if (!state.activity) return;
    state.redirect = true;
}

const getActivity = (id: number) => {
    activiteitRepository.getById(id, platformStore.platformId)
        .then(response => {
            state.activity = response;

            state.bookingUrl = `/activiteit-detail/reserveren?id=${state.activity.SportmomentId}`;
            state.activityUrl = `/activiteit-detail?id=${state.activity.SportmomentId}`;
            state.availableSpots = calculateAvailableSpots.value;
        })
        .catch(() => notify({
            type: 'error',
            title: 'Er is een fout opgetreden bij het ophalen van de activiteit gegevens',
            duration: 20000
        }))
        .finally(() => state.isLoading = false);
}

const isRegistrationStarted = computed(() => {
    if (state.activity.InschrijvenVanafDatum && isBefore(new Date(), state.activity.InschrijvenVanafDatum!)) {
        return false;
    }

    return true;
});

const isRegistrationEnded = computed(() => {
    if (state.activity.InschrijvenTotDatum && isAfter(new Date(), state.activity.InschrijvenTotDatum!)) {
        return true;
    }

    return false;
});

const cancelRedirect = () => {
    state.redirect = false;
}

onBeforeMount(() => {
    getActivity(props.id);
})
</script>

<template>
    <Loader v-if="state.isLoading" :is-active="state.isLoading" />
    <div v-else-if="state.activity" class="sport-card-refined">

        <RedirectPlatformModal v-if="state.redirect"
                               :activityId="state.activity.SportmomentId"
                               :platformUrl="state.activity.HoofdplatformLink"
                               @cancel="cancelRedirect"/>
        
        <div class="sport-card-refined__body">
            <a :href="state.activityUrl" target="_self" class="sport-card-refined__title">
                <h3 class="h5">{{ state.activity.Naam }}</h3>
            </a>
            <div class="sport-card-refined__date-time">
                <div class="sport-card-refined__date-time-icon">
                    <span class="icon-clock" aria-hidden="true"/>
                </div>
                <div class="sport-card-refined__date-time-info">
                    <div class="sport-card-refined__date">
                        {{
                            new Date(state.activity.BeginDatum).toLocaleDateString('nl-NL', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric'
                            })
                        }}
                    </div>
                    <div class="sport-card-refined__divider"></div>
                    <div class="sport-card-refined__time">
                        {{ getDateInHoursAndMinutes(state.activity.BeginDatum) }} - {{ getDateInHoursAndMinutes(state.activity.EindDatum) }}
                    </div>
                </div>
            </div>
            <div class="sport-card-refined__aside">
                <div class="sport-card-refined__aside-top">
                    <div v-if="isRegistrationEnded" class="sport-card-refined__status">
                        <span>De inschrijving is gesloten</span>
                    </div>
                    <div v-else-if="!isRegistrationStarted" class="sport-card-refined__status">
                        <span>Vanaf {{ getDateForRegistrationClosed(state.activity.InschrijvenVanafDatum) }} kan je je inschrijven</span>
                    </div>
                    <div v-else class="sport-card-refined__status" :class="registrationStatusClass">
                        <span v-if="state.availableSpots <= 0">Deze {{ activitySingular }} is vol</span>
                        <span v-else>{{ availableSpotsText }}</span>
                    </div>
                    <div class="sport-card-refined__price">
                        <p class="font-weight-bold mb-0">
                            <span v-if="state.activity.Bekostigingstype === BekostigingsType.CREDITS">1 credit</span>
                            <span v-else-if="state.activity.Bekostigingstype === BekostigingsType.GRATIS">Gratis</span>
                            <span v-else-if="state.activity.Bekostigingstype === BekostigingsType.OPLOCATIE">Betalen op locatie</span>
                            <span v-else class="sport-card-refined__price-costs">
                                <span class="sport-card-refined__price-currency">€</span>
                                <span>{{ (state.activity.Kosten ?? 0).toFixed(2) }}</span>
                            </span>
                        </p>
                    </div>
                </div>
                <div class="sport-card-refined__aside-bottom">
                    <a class="btn btn-sm btn-sport-in-light" :href="state.activityUrl" target="_self">
                        Meer info
                    </a>
                    <button v-if="state.availableSpots >= 1 && !isSamePlatform" @click="startRedirect"
                            class="d-flex align-items-center justify-content-center btn btn-sm btn-outline-primary"
                            :class="(!isRegistrationStarted || isRegistrationEnded) && 'disabled'"
                            :disabled="!isRegistrationStarted || isRegistrationEnded">
                        Boeken&nbsp;
                        <span class="icon icon-external-link" aria-hidden="true"/>
                    </button>
                    <a v-else-if="state.availableSpots >= 1"
                       :href="state.bookingUrl" target="_self"
                       class="d-flex align-items-center justify-content-center btn btn-sm btn-outline-primary"
                       :class="(!isRegistrationStarted || isRegistrationEnded) && 'disabled'">
                        Boeken
                    </a>
                    <a v-if="isRegistrationStarted && !isRegistrationEnded && state.availableSpots < 1"
                       :href="state.activityUrl" target="_self"
                       class="btn btn-sm btn-outline-primary sport-card-refined__waitlist">
                        Wachtrij
                    </a>
                    <Tooltip v-if="state.availableSpots < 1">
                        <span class="icon-info1 ml-1" aria-hidden="true"/>

                        <template #popper>
                            <div class="tooltip-inner" style="background: none; font-size: 0.875rem">
                                Als je jezelf op de wachtrij zet voor deze {{ activitySingular }} en er een plek vrijkomt, ontvang
                                je hierover een e-mail.
                            </div>
                        </template>
                    </Tooltip>
                </div>
            </div>
        </div>
    </div>
</template>
